import { useState } from "react";
import { analyzeMeal } from "../lib/gemini";
import { useAuth } from "../lib/AuthContext";
import { useUserStats } from "../lib/UserStatsContext";
import { useBadgeToast } from "./BadgeToast";
import { AIContent } from "./GIBadge";
import { getUserStats, recordMealAnalysis, checkAndAwardBadges, checkLowGiChampion, checkUsageLimit, recordFeatureUsage } from "../lib/userStats";
import BloodSugarGraph from "./BloodSugarGraph";
import UsageLimitModal from "./UsageLimitModal";
import UsageIndicator from "./UsageIndicator";
import { BackIcon } from "./Icons";

interface MealAnalysis {
  overallGI: "low" | "medium" | "high";
  content: string;
}

const FOOD_CATEGORIES = {
  proteins: {
    icon: "",
    name: "Proteins",
    items: ["Chicken breast", "Salmon", "Eggs", "Tofu", "Beef", "Shrimp", "Tuna", "Turkey"],
  },
  grains: {
    icon: "",
    name: "Grains",
    items: ["White rice", "Brown rice", "Quinoa", "Pasta", "Bread", "Oats", "Barley", "Couscous"],
  },
  vegetables: {
    icon: "",
    name: "Vegetables",
    items: ["Broccoli", "Spinach", "Carrots", "Sweet potato", "Potato", "Green beans", "Peppers", "Tomato"],
  },
  fruits: {
    icon: "",
    name: "Fruits",
    items: ["Apple", "Banana", "Orange", "Berries", "Grapes", "Mango", "Watermelon", "Pineapple"],
  },
  dairy: {
    icon: "",
    name: "Dairy",
    items: ["Greek yogurt", "Milk", "Cheese", "Cottage cheese", "Butter"],
  },
  legumes: {
    icon: "",
    name: "Legumes",
    items: ["Lentils", "Chickpeas", "Black beans", "Kidney beans", "Hummus"],
  },
  fats: {
    icon: "",
    name: "Fats & Nuts",
    items: ["Avocado", "Olive oil", "Almonds", "Walnuts", "Peanut butter"],
  },
};

interface MealBuilderProps {
  onBack?: () => void;
}

export default function MealBuilder({ onBack }: MealBuilderProps) {
  const { user } = useAuth();
  const { dietaryPrefs } = useUserStats();
  const { showBadge } = useBadgeToast();
  const [selectedFoods, setSelectedFoods] = useState<string[]>([]);
  const [customFood, setCustomFood] = useState("");
  const [analysis, setAnalysis] = useState<MealAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>("proteins");
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState<{ currentUsage: number; isPremiumOnly: boolean }>({ currentUsage: 0, isPremiumOnly: false });
  const [usageRefreshKey, setUsageRefreshKey] = useState(0);

  const addFood = (food: string) => {
    if (!selectedFoods.includes(food)) {
      setSelectedFoods([...selectedFoods, food]);
      setAnalysis(null);
    }
  };

  const removeFood = (food: string) => {
    setSelectedFoods(selectedFoods.filter((f) => f !== food));
    setAnalysis(null);
  };

  const addCustomFood = () => {
    if (customFood.trim() && !selectedFoods.includes(customFood.trim())) {
      setSelectedFoods([...selectedFoods, customFood.trim()]);
      setCustomFood("");
      setAnalysis(null);
    }
  };

  const handleAnalyze = async () => {
    if (selectedFoods.length === 0 || !user) return;

    const usageCheck = await checkUsageLimit(user.uid, "mealBuilder");
    if (!usageCheck.allowed) {
      setLimitInfo({ currentUsage: usageCheck.currentUsage, isPremiumOnly: usageCheck.isPremiumOnly });
      setShowLimitModal(true);
      return;
    }

    setIsAnalyzing(true);
    try {
      const result = await analyzeMeal(selectedFoods, dietaryPrefs);
      const giMatch = result.match(/OVERALL_GI:(low|medium|high)/i);
      const overallGI = (giMatch?.[1]?.toLowerCase() as "low" | "medium" | "high") || "medium";
      const content = result.replace(/OVERALL_GI:(low|medium|high)/gi, "").trim();
      setAnalysis({ overallGI, content });

      await recordFeatureUsage(user.uid, "mealBuilder");
      await recordMealAnalysis(user.uid);
      setUsageRefreshKey((k) => k + 1);
      const stats = await getUserStats(user.uid);
      const newBadges = await checkAndAwardBadges(user.uid, stats);
      newBadges.forEach((badge) => showBadge(badge));

      const lowGiBadge = await checkLowGiChampion(user.uid, overallGI === "low");
      if (lowGiBadge) {
        showBadge(lowGiBadge);
      }
    } catch (error) {
      console.error("Error analyzing meal:", error);
      setAnalysis({
        overallGI: "medium",
        content: "Sorry, I had trouble analyzing that meal. Please try again.",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="p-4">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted font-[family-name:--font-ui] mb-4 hover:text-text"
        >
          <BackIcon className="w-4 h-4" /> Back
        </button>
      )}

      <div className="section-label">Meal Builder</div>
      <p className="text-sm text-muted mb-6">Build a meal and see how it affects your blood sugar.</p>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(FOOD_CATEGORIES).map(([key, cat]) => (
          <button
            key={key}
            onClick={() => setActiveCategory(key)}
            className={`px-3 py-1.5 rounded-md text-sm font-[family-name:--font-ui] transition-colors ${
              activeCategory === key
                ? "bg-accent text-white"
                : "border border-border text-text hover:bg-black/5"
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Food items */}
      <div className="flex flex-wrap gap-2 mb-4 min-h-[100px]">
        {FOOD_CATEGORIES[activeCategory as keyof typeof FOOD_CATEGORIES].items.map((food) => (
          <button
            key={food}
            onClick={() => addFood(food)}
            disabled={selectedFoods.includes(food)}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
              selectedFoods.includes(food)
                ? "bg-accent/10 text-accent border border-accent/20 cursor-not-allowed"
                : "border border-border text-text hover:bg-black/5 active:scale-95"
            }`}
          >
            + {food}
          </button>
        ))}
      </div>

      {/* Custom food input */}
      <div className="flex gap-2 mb-6">
        <input
          type="text"
          value={customFood}
          onChange={(e) => setCustomFood(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addCustomFood()}
          placeholder="Add custom food..."
          className="flex-1 px-3 py-2 rounded-md border border-border bg-bg text-text text-sm focus:border-accent outline-none"
        />
        <button
          onClick={addCustomFood}
          disabled={!customFood.trim()}
          className="px-4 py-2 bg-accent text-white rounded-md text-sm font-[family-name:--font-ui] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>

      {/* Your Plate */}
      <div className="border-t border-border pt-4">
        <div className="section-label">Your Plate</div>

        {selectedFoods.length === 0 ? (
          <div className="py-8 text-center text-muted text-sm">
            Add foods to your plate
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-4 min-h-[60px]">
              {selectedFoods.map((food) => (
                <span
                  key={food}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-md text-sm text-text"
                >
                  {food}
                  <button
                    onClick={() => removeFood(food)}
                    className="ml-1 text-muted hover:text-text text-xs"
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>

            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="w-full px-4 py-3 bg-accent text-white rounded-md font-[family-name:--font-ui] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isAnalyzing ? (
                <>
                  <div className="spinner spinner--sm"></div>
                  Analyzing...
                </>
              ) : (
                "Analyze Meal"
              )}
            </button>

            {selectedFoods.length > 0 && (
              <button
                onClick={() => {
                  setSelectedFoods([]);
                  setAnalysis(null);
                }}
                className="w-full mt-2 px-4 py-2 text-muted hover:text-text text-sm font-[family-name:--font-ui]"
              >
                Clear Plate
              </button>
            )}
          </>
        )}
      </div>

      {/* Analysis Results */}
      {analysis && (
        <div className="mt-6 space-y-6">
          <BloodSugarGraph type={analysis.overallGI} label="Expected Blood Sugar Response" />

          <div className="border-t border-border pt-4">
            <AIContent content={analysis.content} />
          </div>
        </div>
      )}

      {/* Usage Indicator */}
      <div className="mt-4 flex justify-center">
        <UsageIndicator feature="mealBuilder" compact onLimitReached={() => setShowLimitModal(true)} refreshKey={usageRefreshKey} />
      </div>

      {/* Usage Limit Modal */}
      <UsageLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        feature="mealBuilder"
        currentUsage={limitInfo.currentUsage}
        isPremiumOnly={limitInfo.isPremiumOnly}
      />
    </div>
  );
}
